import { createTheme } from "@mui/material/styles";
import type { Theme, ThemeOptions } from "@mui/material/styles";
import { BW_COMPONENT_OVERRIDES } from "./componentOverrides";
import { BW_MOTION } from "./motion";
import { BW_DARK_SEMANTIC_ROLES, BW_LIGHT_SEMANTIC_ROLES } from "./roles";

const SYSTEM_FONT_STACK = [
  "Inter",
  "ui-sans-serif",
  "system-ui",
  "-apple-system",
  "BlinkMacSystemFont",
  '"Segoe UI"',
  "sans-serif",
].join(", ");

const BASE_THEME_OPTIONS: ThemeOptions = {
  cssVariables: {
    cssVarPrefix: "bw",
    colorSchemeSelector: "data-bw-color-scheme",
    nativeColor: true,
  },
  colorSchemes: {
    light: {
      palette: {
        secondary: { main: "#52635a" },
        background: {
          default: "#f6f8f6",
        },
        semantic: BW_LIGHT_SEMANTIC_ROLES,
      },
    },
    dark: {
      palette: {
        secondary: { main: "#b8c9bf" },
        background: {
          default: "#0b0f0c",
        },
        semantic: BW_DARK_SEMANTIC_ROLES,
      },
    },
  },
  typography: {
    fontFamily: SYSTEM_FONT_STACK,
    button: {
      fontWeight: 700,
      textTransform: "none",
    },
  },
  shape: {
    borderRadius: 12,
  },
  bw: {
    audience: "base",
    density: "comfortable",
    minimumTouchTarget: 44,
    focus: {
      ringWidth: 2,
      offset: 2,
      color: "var(--bw-palette-semantic-focus)",
    },
    motion: BW_MOTION,
  },
  components: BW_COMPONENT_OVERRIDES,
};

function cloneThemeValue<T>(
  value: T,
  seen = new WeakMap<object, unknown>(),
): T {
  if (value === null || typeof value !== "object") return value;
  const objectValue = value as object;
  const existing = seen.get(objectValue);
  if (existing) return existing as T;

  if (Array.isArray(value)) {
    const clone: unknown[] = [];
    seen.set(objectValue, clone);
    for (const item of value) clone.push(cloneThemeValue(item, seen));
    return clone as T;
  }

  const prototype = Object.getPrototypeOf(value);
  if (prototype !== Object.prototype && prototype !== null) return value;

  const clone: Record<PropertyKey, unknown> = {};
  seen.set(objectValue, clone);
  for (const key of Reflect.ownKeys(objectValue)) {
    clone[key] = cloneThemeValue(
      (value as Record<PropertyKey, unknown>)[key],
      seen,
    );
  }
  return clone as T;
}

function isPlainThemeRecord(value: unknown): value is Record<string, unknown> {
  if (value === null || typeof value !== "object" || Array.isArray(value)) {
    return false;
  }
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
}

function mergeThemeOptions(
  base: ThemeOptions,
  extension: ThemeOptions,
): ThemeOptions {
  const merged = cloneThemeValue(base) as Record<string, unknown>;
  for (const [key, value] of Object.entries(extension)) {
    const baseValue = merged[key];
    merged[key] =
      isPlainThemeRecord(baseValue) && isPlainThemeRecord(value)
        ? mergeThemeOptions(baseValue as ThemeOptions, value as ThemeOptions)
        : cloneThemeValue(value);
  }
  return merged as ThemeOptions;
}

function nativePaletteMappings(theme: Theme): ThemeOptions {
  const mappings: Record<string, unknown> = {};
  for (const scheme of ["light", "dark"] as const) {
    const semantic = theme.colorSchemes[scheme]!.palette.semantic;
    mappings[scheme] = {
      palette: {
        primary: {
          main: semantic.primary,
          contrastText: semantic.onPrimary,
        },
        background: { paper: semantic.surface },
        text: { primary: semantic.onSurface },
        action: { selected: semantic.selected },
        error: { main: semantic.error },
        success: { main: semantic.success },
        warning: { main: semantic.warning },
      },
    };
  }
  return { colorSchemes: mappings } as ThemeOptions;
}

function composeTheme(extension: ThemeOptions): Theme {
  const semanticOptions = mergeThemeOptions(
    mergeThemeOptions(BASE_THEME_OPTIONS, extension),
    { cssVariables: BASE_THEME_OPTIONS.cssVariables },
  );
  const semanticTheme = createTheme(semanticOptions);
  const completeOptions = mergeThemeOptions(
    semanticOptions,
    nativePaletteMappings(semanticTheme),
  );
  return createTheme(completeOptions);
}

const canonicalBaseTheme = composeTheme({});

export function createBwTheme(extension: ThemeOptions = {}): Theme {
  // createTheme performs a deep composition and returns a fresh object. Keeping
  // the base options readonly-by-convention prevents one app theme from
  // mutating another app's contract.
  if (Reflect.ownKeys(extension).length === 0) {
    return cloneThemeValue(canonicalBaseTheme);
  }
  return composeTheme(extension);
}
